import { Head, useForm } from '@inertiajs/react';
import { useState, useRef } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { ArrowLeftIcon, PaperAirplaneIcon, PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function EmailComposer({ visitor, subject: defaultSubject, emailBody: defaultBody, flash }) {
    const fileInputRef = useRef(null);
    const [newFileNames, setNewFileNames] = useState([]);
    const [selectedVisitorAttachments, setSelectedVisitorAttachments] = useState([]);

    const { data, setData, post, processing, errors, progress } = useForm({
        subject: defaultSubject || '',
        body: defaultBody || '',
        include_visitor_attachments: [],
        attachments: [],
    });

    const toggleVisitorAttachment = (path) => {
        const updated = selectedVisitorAttachments.includes(path)
            ? selectedVisitorAttachments.filter(p => p !== path)
            : [...selectedVisitorAttachments, path];
        setSelectedVisitorAttachments(updated);
        setData('include_visitor_attachments', updated);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setData('attachments', files);
        setNewFileNames(files.map(f => f.name));
    };

    const removeNewFiles = () => {
        setData('attachments', []);
        setNewFileNames([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const submit = (e) => {
        e.preventDefault();
        post(`/visitors/${visitor.id}/send-email`, {
            forceFormData: true,
        });
    };

    return (
        <AppLayout>
            <Head title={`Email: ${visitor.personal_contact_name || visitor.email}`} />
            <PageHeader
                title="Compose Email"
                subtitle={`To: ${visitor.email}`}
                actions={
                    <Button href={`/visitors/${visitor.id}`} variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back to Visitor
                    </Button>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            <form onSubmit={submit} className="space-y-6">
                {/* Recipient Info */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Recipient</h3>
                    </div>
                    <CardBody>
                        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-900 font-bold text-sm">
                                {(visitor.personal_contact_name || visitor.email || '?').charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <p className="text-sm font-medium text-slate-800">{visitor.personal_contact_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500">{visitor.email}</p>
                                {visitor.company_name && <p className="text-xs text-slate-400">{visitor.company_name}</p>}
                            </div>
                            {visitor.exhibition && (
                                <div className="ml-auto">
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{visitor.exhibition.name}</span>
                                </div>
                            )}
                        </div>
                    </CardBody>
                </Card>

                {/* Email Content */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Email Content</h3>
                    </div>
                    <CardBody className="space-y-4">
                        <FormInput
                            label="Subject"
                            value={data.subject}
                            onChange={e => setData('subject', e.target.value)}
                            error={errors.subject}
                            required
                        />
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Message Body <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={data.body}
                                onChange={e => setData('body', e.target.value)}
                                rows={15}
                                className={`w-full rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20 transition px-4 py-2.5 font-mono ${errors.body ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                            />
                            {errors.body && <p className="mt-1 text-xs text-red-500">{errors.body}</p>}
                            <p className="mt-1 text-xs text-slate-400">Supports HTML content</p>
                        </div>
                    </CardBody>
                </Card>

                {/* Visitor Registration Attachments */}
                {visitor.attachments && visitor.attachments.length > 0 && (
                    <Card>
                        <div className="px-6 py-4 border-b border-slate-100">
                            <h3 className="font-semibold text-slate-800">Include Visitor Attachments</h3>
                            <p className="text-xs text-slate-400 mt-1">Select attachments from the visitor's registration to include in the email</p>
                        </div>
                        <CardBody>
                            <div className="space-y-2">
                                {visitor.attachments.map((att, i) => (
                                    <label
                                        key={i}
                                        className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                                            selectedVisitorAttachments.includes(att.path)
                                                ? 'bg-emerald-50 border border-emerald-300'
                                                : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedVisitorAttachments.includes(att.path)}
                                            onChange={() => toggleVisitorAttachment(att.path)}
                                            className="rounded border-slate-300 text-emerald-700 focus:ring-emerald-700"
                                        />
                                        <PaperClipIcon className="w-4 h-4 text-slate-400" />
                                        <span className="text-sm text-slate-700 flex-1 truncate">{att.name}</span>
                                        <span className="text-xs text-slate-400">
                                            {att.size ? `${(att.size / 1024).toFixed(1)} KB` : ''}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Additional Attachments */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Additional Attachments</h3>
                    </div>
                    <CardBody>
                        <div className="space-y-3">
                            <div
                                className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-emerald-600 transition-colors"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <PaperClipIcon className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                                <p className="text-sm text-slate-600">Click to attach files</p>
                                <p className="text-xs text-slate-400 mt-1">Max 10MB per file</p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            {newFileNames.length > 0 && (
                                <div className="space-y-2">
                                    {newFileNames.map((name, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600 bg-emerald-50 px-3 py-2 rounded-lg">
                                            <PaperClipIcon className="w-4 h-4 text-emerald-700" />
                                            <span className="flex-1 truncate">{name}</span>
                                        </div>
                                    ))}
                                    <button type="button" onClick={removeNewFiles} className="text-xs text-red-500 hover:text-red-700">
                                        <XMarkIcon className="w-3 h-3 inline" /> Remove files
                                    </button>
                                </div>
                            )}
                            {progress && (
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                    <div className="bg-emerald-700 h-2 rounded-full transition-all" style={{ width: `${progress.percentage}%` }} />
                                </div>
                            )}
                        </div>
                    </CardBody>
                </Card>

                {/* Submit */}
                <div className="flex gap-3">
                    <Button type="submit" disabled={processing}>
                        <PaperAirplaneIcon className="w-4 h-4" />
                        {processing ? 'Sending...' : 'Send Email'}
                    </Button>
                    <Button href={`/visitors/${visitor.id}`} variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
